/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import NextLink from 'next/link';
import cn from 'classnames';

import { Highlight } from 'ui/atoms/Highlight/Highlight';
import { Section } from 'ui/molecules/Section/Section';

import styles from './EnrichmentUseCasesSection.module.scss';

const USE_CASES = [
  {
    n: '01',
    title: 'Product enrichment',
    short: 'A licensed data feed in your warehouse, with updates as new filings publish.',
    long: 'A SaaS platform loads the feed into its own database and serves company lookups from it. Update files land on schedule, so records never drift.',
    href: '/contact-us',
  },
  {
    n: '02',
    title: 'CRM enrichment',
    short: 'Backfill the accounts you already have, then auto-enrich new records as they’re created.',
    long: 'A rep types a company name and the account fills itself. Everything already sitting in the CRM gets the same pass, then stays current on its own.',
    href: '/salesforce-integration',
  },
  {
    n: '03',
    title: 'Master data',
    short: 'Registry-verified names, IDs and ownership as your golden record.',
    long: 'Three systems spell the same customer three ways. Keying all of them to one registration number ends the argument.',
    href: '/data-enrichment',
  },
  {
    n: '04',
    title: 'Signup form enrichment',
    short: 'A visitor types their company name and the form fills itself.',
    long: 'Signup drops from nine fields to two, and the business is verified before a human ever reviews it.',
    href: 'https://api.globaldatabase.com/docs/v2/',
  },
  {
    n: '05',
    title: 'Claude or ChatGPT enrichment',
    short: 'Add the connector and enrich records straight from a chat.',
    long: 'An analyst drops a list of company names into Claude and gets registration numbers, status and filed revenue back, each one traceable to the filing it came from.',
    href: '/global-database-is-now-live-on-claude-free-to-install',
  },
  {
    n: '06',
    title: 'Lead routing & scoring',
    short: 'Route on filed revenue, employee counts and industry codes.',
    long: 'A lender sends anything over £10M filed revenue straight to enterprise reps instead of a guessed employee band.',
    href: '/salesforce-integration',
  },
] as const;

export const EnrichmentUseCasesSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Section
      extra={
        <Highlight color="blue" font="mono" uppercase>
          Use cases
        </Highlight>
      }
      title="How teams use enrichment"
    >
      <div ref={ref} className={cn(styles.list, revealed && styles.revealed)}>
        {USE_CASES.map((uc, i) => {
          const content = (
            <>
              <span className={styles.rail} aria-hidden="true" />
              <span className={styles.n}>{uc.n}</span>
              <span className={styles.t}>{uc.title}</span>

              <span className={styles.c}>
                <span className={styles.d}>{uc.short}</span>
                <span className={styles.e}>{uc.long}</span>
              </span>

              <span className={styles.arw} aria-hidden="true">
                →
              </span>
            </>
          );

          const rowProps = {
            className: styles.row,
            style: { transitionDelay: `${i * 60}ms` },
          };

          return uc.href.startsWith('http') ? (
            <a key={uc.n} href={uc.href} target="_blank" rel="noopener noreferrer" {...rowProps}>
              {content}
            </a>
          ) : (
            <NextLink key={uc.n} href={uc.href} {...rowProps}>
              {content}
            </NextLink>
          );
        })}
      </div>
    </Section>
  );
};
